import { DISPLAY_CURRENCIES, ICON_TYPES } from "@shared/common/constants";
import { useSelector } from "react-redux";
import React, { CSSProperties, ReactNode, useMemo } from "react";
import { DisplayCurrency } from "@shared/types";
import Flex from "common/Boxes/Flex";
import IconWithFallback, {
  IconWithFallbackProps,
} from "Global/IconWithFallback";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { BTC } from "common/Icons/Icons";

export interface TextWithIconProps extends IconWithFallbackProps {
  text?: ReactNode;
  justify?: string;
  currency?: DisplayCurrency | null;
  style?: CSSProperties;
  textStyle?: CSSProperties;
  pxIconSize?: number;
  /** Shown before the currency icon, e.g. the minus of a negative amount. */
  sign?: ReactNode;
}

const TextWithIcon = (props: TextWithIconProps) => {
  const {
    text,
    iconLink,
    iconType = ICON_TYPES.DISPLAY_CURRENCY,
    iconStyle,
    iconSize,
    coinOrExchangeName,
    justify,
    currency,
    style,
    textStyle,
    pxIconSize,
    sign,
  } = props;
  // Callers that pass an image link (e.g. a group without a picture) never
  // want the currency sign as a fallback.
  const expectsImage = "iconLink" in props;
  let displayCurrency = useSelector(selectDisplayCurrency);
  if (currency) displayCurrency = currency;

  const finalIconStyle = useMemo(() => {
    return (
      iconStyle || {
        marginRight: 6,
      }
    );
  }, [iconStyle]);

  let iconElement: ReactNode;
  if (iconType === ICON_TYPES.DISPLAY_CURRENCY && !iconLink && !expectsImage) {
    iconElement =
      displayCurrency === DISPLAY_CURRENCIES.BTC ? (
        <BTC style={{ marginRight: 2, ...iconStyle }} />
      ) : (
        <span style={iconStyle}>$</span>
      );
  } else if (
    iconType === ICON_TYPES.COIN ||
    iconType === ICON_TYPES.EXCHANGE ||
    iconLink
  ) {
    iconElement = (
      <IconWithFallback
        coinOrExchangeName={coinOrExchangeName}
        iconType={iconType}
        iconStyle={finalIconStyle}
        iconSize={iconSize}
        pxIconSize={pxIconSize}
        iconLink={iconLink}
      />
    );
  }

  return (
    <Flex
      $alignItems={"center"}
      $justifyContent={justify}
      $inline={true}
      style={{ verticalAlign: "top", ...style }}
    >
      {sign}
      {iconElement}
      <span
        style={{
          overflow: "hidden",
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          ...textStyle,
        }}
      >
        {text}
      </span>
    </Flex>
  );
};

export default TextWithIcon;
